import { AuthBaseClient } from '../base-client';
import type {
  AppointmentTypeResponse,
  CreateAppointmentTypeRequest,
  UpdateAppointmentTypeRequest,
  AppointmentTypeOverrideResponse,
  UpsertOverrideRequest,
  AppointmentTypeEligibilityResponse,
} from './types';

/**
 * Client for appointment type API endpoints.
 * Used by frontend for appointment type settings management.
 */
export class AppointmentTypeClient extends AuthBaseClient {
  /**
   * List all appointment types for the organization.
   */
  async list(): Promise<AppointmentTypeResponse[]> {
    return this.get<AppointmentTypeResponse[]>('/api/appointment-types');
  }

  /**
   * Create a new appointment type.
   */
  async create(data: CreateAppointmentTypeRequest): Promise<AppointmentTypeResponse> {
    return this.post<AppointmentTypeResponse>('/api/appointment-types', data);
  }

  /**
   * Get a single appointment type by ID.
   */
  async getById(id: string): Promise<AppointmentTypeResponse> {
    return this.get<AppointmentTypeResponse>(`/api/appointment-types/${id}`);
  }

  /**
   * Update an appointment type.
   */
  async update(id: string, data: UpdateAppointmentTypeRequest): Promise<AppointmentTypeResponse> {
    return this.patch<AppointmentTypeResponse>(`/api/appointment-types/${id}`, data);
  }

  /**
   * Delete (soft-delete) an appointment type.
   */
  async remove(id: string): Promise<void> {
    await this.delete(`/api/appointment-types/${id}`);
  }

  /**
   * Get all overrides for a specific appointment type.
   */
  async getOverrides(appointmentTypeId: string): Promise<AppointmentTypeOverrideResponse[]> {
    return this.get<AppointmentTypeOverrideResponse[]>(
      `/api/appointment-types/${appointmentTypeId}/overrides`
    );
  }

  /**
   * Get overrides by provider (and optionally location).
   */
  async getOverridesByProvider(
    providerId: string,
    locationId?: string
  ): Promise<AppointmentTypeOverrideResponse[]> {
    const params = new URLSearchParams({ providerId });
    if (locationId) params.set('locationId', locationId);
    return this.get<AppointmentTypeOverrideResponse[]>(
      `/api/appointment-type-overrides?${params.toString()}`
    );
  }

  /**
   * Upsert an override.
   */
  async upsertOverride(data: UpsertOverrideRequest): Promise<AppointmentTypeOverrideResponse> {
    return this.put<AppointmentTypeOverrideResponse>('/api/appointment-type-overrides', data);
  }

  /**
   * Remove an override.
   */
  async removeOverride(id: string): Promise<void> {
    await this.delete(`/api/appointment-type-overrides/${id}`);
  }
}
